// ============================================================
// OTP Gate Component
// Reusable email + 6-digit OTP verification, used identically
// for both the free trial and the paid checkout flow.
// ============================================================

import React, { useState } from 'react';
import { requestOtp, verifyOtp } from '../services/api';

function OtpGate({ purpose, onVerified }) {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [stage, setStage] = useState('email'); // email -> code -> done
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSendCode(e) {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await requestOtp(email, purpose);
      setStage('code');
    } catch (err) {
      setError('Could not send verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyCode(e) {
    e.preventDefault();
    if (!code || code.length !== 6) {
      setError('Please enter the 6-digit code sent to your email.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await verifyOtp(email, code, purpose);
      if (!result.verified) {
        setError(result.error || 'Invalid or expired code. Please try again.');
        setLoading(false);
        return;
      }
      onVerified(email);
    } catch (err) {
      setError('Could not verify code. Please try again.');
      setLoading(false);
    }
  }

  return (
    <div className="otp-gate">
      <p className="otp-note">
        We verify your email with a quick 6-digit code — this keeps the assessment fair and free of automated abuse.
        {purpose === 'trial' && ' Your email is used only for this verification and is not stored anywhere.'}
      </p>

      {stage === 'email' && (
        <form onSubmit={handleSendCode}>
          <label>
            Email address
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
          </label>
          {error && <p className="error">{error}</p>}
          <button type="submit" disabled={loading}>
            {loading ? 'Sending...' : 'Send verification code'}
          </button>
        </form>
      )}

      {stage === 'code' && (
        <form onSubmit={handleVerifyCode}>
          <p className="otp-sent-to">Code sent to <strong>{email}</strong></p>
          <p className="otp-spam-hint">Didn't receive the code? Check your spam or junk folder — it can take a minute to arrive.</p>
          <label>
            6-digit code
            <input
              type="text"
              inputMode="numeric"
              maxLength="6"
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="123456"
            />
          </label>
          {error && <p className="error">{error}</p>}
          <button type="submit" disabled={loading}>
            {loading ? 'Verifying...' : 'Verify & Continue'}
          </button>
          <button type="button" className="link-button" onClick={() => setStage('email')}>
            Use a different email
          </button>
        </form>
      )}
    </div>
  );
}

export default OtpGate;
